import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogContent,
  Box,
  Typography,
  TextField,
  Button,
  IconButton,
  Grid,
  Stack,
  InputAdornment,
  Fade,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import PersonIcon from "@mui/icons-material/Person";
import PhoneIcon from "@mui/icons-material/Phone";
import CalendarMonthIcon from "@mui/icons-material/CalendarMonth";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import WhatsAppIcon from "@mui/icons-material/WhatsApp";
import TwoWheelerIcon from "@mui/icons-material/TwoWheeler";
import { useBooking } from "../context/BookingContext";

const PHONE_TEL = "918123198675";
const VEHICLE_NAME = "Blaze";
const VEHICLE_PRICE = "₹1,999 / week";

const getInitialDates = () => {
  const now = new Date();
  now.setMinutes(0, 0, 0);
  now.setHours(now.getHours() + 1);
  const startIso = new Date(now.getTime() - now.getTimezoneOffset() * 60000).toISOString().slice(0, 16);

  const end = new Date(now.getTime() + 7 * 24 * 60 * 60 * 1000); // 7 days later
  const endIso = new Date(end.getTime() - end.getTimezoneOffset() * 60000).toISOString().slice(0, 16);

  return { startIso, endIso };
};

export default function BookingModal() {
  const { isOpen, closeBookingModal } = useBooking();

  const [name, setName] = useState("");
  const [phone, setPhone] = useState("");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [submitted, setSubmitted] = useState(false);
  const [errors, setErrors] = useState({});

  useEffect(() => {
    if (isOpen) {
      const { startIso, endIso } = getInitialDates();
      setStartDate(startIso);
      setEndDate(endIso);
      setSubmitted(false);
      setErrors({});
    }
  }, [isOpen]);

  // Calculate rental duration
  const getDurationText = () => {
    if (!startDate || !endDate) return null;
    const start = new Date(startDate);
    const end = new Date(endDate);
    const diffMs = end - start;
    if (isNaN(diffMs) || diffMs <= 0) return "Please select a valid end date after start date.";

    const totalHours = Math.floor(diffMs / (1000 * 60 * 60));
    const days = Math.floor(totalHours / 24);
    const remainingHours = totalHours % 24;

    let parts = [];
    if (days > 0) parts.push(`${days} Day${days > 1 ? "s" : ""}`);
    if (remainingHours > 0) parts.push(`${remainingHours} Hour${remainingHours > 1 ? "s" : ""}`);

    return parts.length > 0 ? parts.join(", ") : "Less than 1 Hour";
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    const newErrors = {};
    if (!name.trim()) newErrors.name = "Name is required";
    if (!phone.trim() || phone.trim().length < 10) newErrors.phone = "Enter a valid 10-digit phone number";
    if (!startDate) newErrors.startDate = "Start date & time required";
    if (!endDate) newErrors.endDate = "Last date & time required";

    if (startDate && endDate && new Date(endDate) <= new Date(startDate)) {
      newErrors.endDate = "Last time must be after start time";
    }

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }

    setSubmitted(true);
  };

  const durationStr = getDurationText();

  const handleWhatsAppBooking = () => {
    const message = `Hello RideX City! I want to book a ride.%0A%0A*Name:* ${encodeURIComponent(name)}%0A*Phone:* ${encodeURIComponent(phone)}%0A*Vehicle:* ${encodeURIComponent(VEHICLE_NAME)} (${VEHICLE_PRICE})%0A*Start:* ${encodeURIComponent(startDate.replace("T", " "))}%0A*Last Date:* ${encodeURIComponent(endDate.replace("T", " "))}%0A*Duration:* ${encodeURIComponent(durationStr)}`;
    window.open(`https://wa.me/${PHONE_TEL}?text=${message}`, "_blank");
  };

  return (
    <Dialog
      open={isOpen}
      onClose={closeBookingModal}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: "20px",
          bgcolor: "#FFFFFF",
          color: "#0F172A",
          boxShadow: "0 20px 50px rgba(0,0,0,0.14)",
          border: "1px solid #E2E8F0",
          overflow: "hidden",
          position: "relative",
        },
      }}
    >
      {/* Top Brand Accent Line */}
      <Box sx={{ height: "4px", bgcolor: "#1B4ED8" }} />

      {/* Close Button */}
      <IconButton
        onClick={closeBookingModal}
        sx={{
          position: "absolute",
          top: 16,
          right: 16,
          color: "#64748B",
          bgcolor: "#F1F5F9",
          "&:hover": { bgcolor: "#E2E8F0", color: "#0F172A" },
          zIndex: 10,
        }}
      >
        <CloseIcon fontSize="small" />
      </IconButton>

      <DialogContent sx={{ p: { xs: 3, sm: 4 } }}>
        {!submitted ? (
          <Fade in={!submitted}>
            <Box component="form" onSubmit={handleSubmit}>
              {/* Header */}
              <Box sx={{ mb: 3 }}>
                <Box
                  sx={{
                    display: "inline-flex",
                    alignItems: "center",
                    gap: 0.8,
                    px: 1.5,
                    py: 0.5,
                    borderRadius: "20px",
                    bgcolor: "rgba(27, 78, 216, 0.08)",
                    border: "1px solid rgba(27, 78, 216, 0.2)",
                    color: "#1B4ED8",
                    fontSize: 12,
                    fontWeight: 700,
                    letterSpacing: "0.04em",
                    mb: 1.2,
                  }}
                >
                  <TwoWheelerIcon sx={{ fontSize: 16 }} />
                  BOOK YOUR ELECTRIC SCOOTER
                </Box>
                <Typography
                  variant="h5"
                  sx={{
                    fontFamily: "'Space Grotesk', sans-serif",
                    fontWeight: 700,
                    fontSize: { xs: 22, sm: 26 },
                    color: "#0F172A",
                    mb: 0.5,
                  }}
                >
                  RideX Scooter Booking
                </Typography>
                <Typography sx={{ color: "#64748B", fontSize: 14 }}>
                  Enter your details below to schedule your electric scooter.
                </Typography>
              </Box>

              {/* Single Vehicle Info Card */}
              <Box
                sx={{
                  p: 2,
                  mb: 3,
                  borderRadius: "14px",
                  bgcolor: "#F8FAFC",
                  border: "1px solid #E2E8F0",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                }}
              >
                <Stack direction="row" spacing={1.5} alignItems="center">
                  <Box
                    sx={{
                      width: 44,
                      height: 44,
                      borderRadius: "10px",
                      bgcolor: "rgba(27, 78, 216, 0.08)",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                    }}
                  >
                    <TwoWheelerIcon sx={{ color: "#1B4ED8", fontSize: 24 }} />
                  </Box>
                  <Box>
                    <Typography sx={{ fontWeight: 700, fontSize: 15, color: "#0F172A" }}>
                      {VEHICLE_NAME}
                    </Typography>
                    <Typography sx={{ fontSize: 12, color: "#64748B" }}>
                      65 km/h top speed · 120 km range
                    </Typography>
                  </Box>
                </Stack>
                <Box
                  sx={{
                    px: 1.75,
                    py: 0.6,
                    borderRadius: "8px",
                    bgcolor: "#1B4ED8",
                    color: "#FFFFFF",
                    fontWeight: 700,
                    fontSize: 13,
                  }}
                >
                  {VEHICLE_PRICE}
                </Box>
              </Box>

              <Grid container spacing={2.5}>
                {/* Full Name */}
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    required
                    label="Full Name"
                    placeholder="Enter your name"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    error={!!errors.name}
                    helperText={errors.name}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <PersonIcon sx={{ color: "#64748B" }} />
                        </InputAdornment>
                      ),
                    }}
                    sx={{
                      "& .MuiOutlinedInput-root": {
                        borderRadius: "12px",
                        bgcolor: "#F8FAFC",
                        color: "#0F172A",
                        "& fieldset": { borderColor: "#E2E8F0" },
                        "&:hover fieldset": { borderColor: "#1B4ED8" },
                        "&.Mui-focused fieldset": { borderColor: "#1B4ED8" },
                      },
                      "& .MuiInputLabel-root": { color: "#64748B" },
                    }}
                  />
                </Grid>

                {/* Phone Number */}
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    required
                    label="Phone Number"
                    placeholder="10-digit phone number"
                    value={phone}
                    onChange={(e) => setPhone(e.target.value)}
                    error={!!errors.phone}
                    helperText={errors.phone}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <PhoneIcon sx={{ color: "#64748B" }} />
                        </InputAdornment>
                      ),
                    }}
                    sx={{
                      "& .MuiOutlinedInput-root": {
                        borderRadius: "12px",
                        bgcolor: "#F8FAFC",
                        color: "#0F172A",
                        "& fieldset": { borderColor: "#E2E8F0" },
                        "&:hover fieldset": { borderColor: "#1B4ED8" },
                        "&.Mui-focused fieldset": { borderColor: "#1B4ED8" },
                      },
                      "& .MuiInputLabel-root": { color: "#64748B" },
                    }}
                  />
                </Grid>

                {/* Start Date & Time */}
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    required
                    type="datetime-local"
                    label="Start Date & Time"
                    value={startDate}
                    onChange={(e) => setStartDate(e.target.value)}
                    error={!!errors.startDate}
                    helperText={errors.startDate}
                    InputLabelProps={{ shrink: true }}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <CalendarMonthIcon sx={{ color: "#72BF20" }} />
                        </InputAdornment>
                      ),
                    }}
                    sx={{
                      "& .MuiOutlinedInput-root": {
                        borderRadius: "12px",
                        bgcolor: "#F8FAFC",
                        color: "#0F172A",
                        "& fieldset": { borderColor: "#E2E8F0" },
                        "&:hover fieldset": { borderColor: "#72BF20" },
                        "&.Mui-focused fieldset": { borderColor: "#72BF20" },
                      },
                      "& .MuiInputLabel-root": { color: "#64748B" },
                    }}
                  />
                </Grid>

                {/* Last Date & Time */}
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    required
                    type="datetime-local"
                    label="Last Date & Time"
                    value={endDate}
                    onChange={(e) => setEndDate(e.target.value)}
                    error={!!errors.endDate}
                    helperText={errors.endDate}
                    InputLabelProps={{ shrink: true }}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <CalendarMonthIcon sx={{ color: "#1B4ED8" }} />
                        </InputAdornment>
                      ),
                    }}
                    sx={{
                      "& .MuiOutlinedInput-root": {
                        borderRadius: "12px",
                        bgcolor: "#F8FAFC",
                        color: "#0F172A",
                        "& fieldset": { borderColor: "#E2E8F0" },
                        "&:hover fieldset": { borderColor: "#1B4ED8" },
                        "&.Mui-focused fieldset": { borderColor: "#1B4ED8" },
                      },
                      "& .MuiInputLabel-root": { color: "#64748B" },
                    }}
                  />
                </Grid>
              </Grid>

              {/* Duration Info Pill */}
              {durationStr && (
                <Box
                  sx={{
                    mt: 2.5,
                    p: 1.75,
                    borderRadius: "12px",
                    bgcolor: "rgba(114, 191, 32, 0.1)",
                    border: "1px solid rgba(114, 191, 32, 0.3)",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                  }}
                >
                  <Typography sx={{ fontSize: 13, color: "#334155", fontWeight: 600 }}>
                    Rental Duration:
                  </Typography>
                  <Typography sx={{ fontSize: 14, color: "#72BF20", fontWeight: 700, fontFamily: "'Space Grotesk', sans-serif" }}>
                    {durationStr}
                  </Typography>
                </Box>
              )}

              {/* Submit Button */}
              <Button
                type="submit"
                variant="contained"
                fullWidth
                size="large"
                sx={{
                  mt: 3,
                  py: 1.5,
                  borderRadius: "12px",
                  fontSize: 16,
                  fontWeight: 700,
                  fontFamily: "'Space Grotesk', sans-serif",
                  bgcolor: "#1B4ED8",
                  color: "#FFFFFF",
                  boxShadow: "0 6px 20px rgba(27, 78, 216, 0.25)",
                  "&:hover": {
                    bgcolor: "#1338A8",
                    boxShadow: "0 10px 25px rgba(27, 78, 216, 0.35)",
                  },
                }}
              >
                Confirm Booking Request
              </Button>
            </Box>
          </Fade>
        ) : (
          /* Confirmation State */
          <Fade in={submitted}>
            <Box sx={{ textAlign: "center", py: 3 }}>
              <Box
                sx={{
                  width: 72,
                  height: 72,
                  borderRadius: "50%",
                  bgcolor: "rgba(114, 191, 32, 0.12)",
                  border: "2px solid #72BF20",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  mx: "auto",
                  mb: 2.5,
                }}
              >
                <CheckCircleOutlineIcon sx={{ fontSize: 44, color: "#72BF20" }} />
              </Box>

              <Typography
                variant="h4"
                sx={{
                  fontFamily: "'Space Grotesk', sans-serif",
                  fontWeight: 700,
                  color: "#0F172A",
                  mb: 1,
                  fontSize: 26,
                }}
              >
                Booking Request Sent!
              </Typography>
              <Typography sx={{ color: "#64748B", fontSize: 15, maxWidth: 420, mx: "auto", mb: 3 }}>
                Thank you, <Box component="span" sx={{ color: "#0F172A", fontWeight: 700 }}>{name}</Box>! Our RideX City team will contact you on{" "}
                <Box component="span" sx={{ color: "#1B4ED8", fontWeight: 700 }}>{phone}</Box> to confirm your {VEHICLE_NAME} booking.
              </Typography>

              {/* Booking Summary Box */}
              <Box
                sx={{
                  p: 2.5,
                  borderRadius: "14px",
                  bgcolor: "#F8FAFC",
                  border: "1px solid #E2E8F0",
                  textAlign: "left",
                  mb: 3.5,
                }}
              >
                <Typography sx={{ fontSize: 12, color: "#64748B", fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.05em", mb: 1.5 }}>
                  Booking Details
                </Typography>
                <Grid container spacing={1.5}>
                  <Grid item xs={6}>
                    <Typography sx={{ fontSize: 12, color: "#64748B" }}>Vehicle</Typography>
                    <Typography sx={{ fontSize: 14, fontWeight: 700, color: "#0F172A" }}>{VEHICLE_NAME}</Typography>
                  </Grid>
                  <Grid item xs={6}>
                    <Typography sx={{ fontSize: 12, color: "#64748B" }}>Rate</Typography>
                    <Typography sx={{ fontSize: 14, fontWeight: 700, color: "#1B4ED8" }}>{VEHICLE_PRICE}</Typography>
                  </Grid>
                  <Grid item xs={6}>
                    <Typography sx={{ fontSize: 12, color: "#64748B" }}>Start Date &amp; Time</Typography>
                    <Typography sx={{ fontSize: 13, fontWeight: 600, color: "#0F172A" }}>{startDate.replace("T", " ")}</Typography>
                  </Grid>
                  <Grid item xs={6}>
                    <Typography sx={{ fontSize: 12, color: "#64748B" }}>Last Date &amp; Time</Typography>
                    <Typography sx={{ fontSize: 13, fontWeight: 600, color: "#0F172A" }}>{endDate.replace("T", " ")}</Typography>
                  </Grid>
                  <Grid item xs={12}>
                    <Typography sx={{ fontSize: 12, color: "#64748B" }}>Duration</Typography>
                    <Typography sx={{ fontSize: 13, fontWeight: 700, color: "#72BF20" }}>{durationStr}</Typography>
                  </Grid>
                </Grid>
              </Box>

              <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
                <Button
                  variant="contained"
                  fullWidth
                  startIcon={<WhatsAppIcon />}
                  onClick={handleWhatsAppBooking}
                  sx={{
                    py: 1.3,
                    borderRadius: "12px",
                    fontWeight: 700,
                    fontSize: 14.5,
                    bgcolor: "#25D366",
                    color: "#FFFFFF",
                    boxShadow: "0 4px 14px rgba(37, 211, 102, 0.3)",
                    "&:hover": { bgcolor: "#1EBE5A" },
                  }}
                >
                  Send via WhatsApp
                </Button>
                <Button
                  variant="outlined"
                  fullWidth
                  onClick={closeBookingModal}
                  sx={{
                    py: 1.3,
                    borderRadius: "12px",
                    fontWeight: 700,
                    fontSize: 14.5,
                    borderColor: "#CBD5E1",
                    color: "#475569",
                    "&:hover": { borderColor: "#0F172A", color: "#0F172A", bgcolor: "#F8FAFC" },
                  }}
                >
                  Close
                </Button>
              </Stack>
            </Box>
          </Fade>
        )}
      </DialogContent>
    </Dialog>
  );
}
